import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { InmateApi } from '@/api';
import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { IInmate } from '@/pages/JMS/Managament/types/inmate';
import { JmsManagementRoutes } from '@/pages/JMS/Managament/utils/utils';

interface InmateDataGridProps {
  cta: React.ReactNode;
  columnSettings: DataGridColSettings[];
  setColumnSettings: (settings: DataGridColSettings[]) => void;
  onOpenPanel: (
    e: React.MouseEvent<HTMLButtonElement>,
    inmate?: IInmate,
  ) => void;
}

/**
 * Note the separate var name (InmateDataGridComponent) so we can
 * keep the export clean:  export const InmateDataGrid = memo(…)
 */
const InmateDataGridComponent = ({
  cta,
  columnSettings,
  setColumnSettings,
  onOpenPanel,
}: InmateDataGridProps) => {
  const navigate = useNavigate();

  /* ----------  stable callbacks  ---------- */
  const fetchInmates = useCallback(
    async (page = 1, limit = 10, filter = '', sort = '') => {
      try {
        const { data } = await InmateApi.list({ page, limit, filter, sort });
        return { rows: data.docs, totalCount: data.totalDocs };
      } catch {
        return { rows: [], totalCount: 0 };
      }
    },
    [], // ↳ zero deps → same reference between renders
  );

  const handleView = useCallback(
    (inmate?: IInmate) =>
      navigate(
        `${JmsManagementRoutes.ViewInmateContextIdentity(
          inmate?._id,
        )}&context=inmate`,
      ),
    [navigate],
  );

  const handleEdit = useCallback(
    (inmate?: IInmate) =>
      navigate(
        `${JmsManagementRoutes.EditInmateContextIdentity(
          inmate?._id,
        )}?context=inmate`,
      ),
    [navigate],
  );

  const columns = useDataGridColumns({
    type: 'inmate',
    columnSettings,
    onView: handleView,
    onEdit: handleEdit,
    openMenu: onOpenPanel,
    navigate,
  });

  return (
    <DataGrid
      name="inmate"
      columns={columns}
      columnSettings={columnSettings}
      setColumnSettings={setColumnSettings}
      fetchData={fetchInmates}
      cta={cta}
    />
  );
};

export const InmateDataGrid = memo(InmateDataGridComponent);
